// @ts-nocheck
import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { CreditCard, Info, Pencil } from 'lucide-react';
import SectionCard from './SectionCard';
import InlineProductForm from './InlineProductForm';
import autoProductName from '@/utils/autoProductName';

const convertToUSD = (amount) => (amount / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const CREATE_VALUE = '__create__';

// One-line summary of the selected fee so the admin sees what a product means
// without opening the edit form.
const feeSummary = (p) => {
  if (!p) return '';
  const parts = [`${convertToUSD(p.amount || 0)} ${p.perPlayer ? 'per player' : 'per team'}`];
  if (p.perPlayer && p.playerPaySeparately) parts.push('players pay separately');
  if (p.earlyBirdAmount != null) parts.push(`early bird ${convertToUSD(p.earlyBirdAmount)}`);
  if (p.lateRegistrationAmount != null) parts.push(`late ${convertToUSD(p.lateRegistrationAmount)}`);
  if (p.additionalTeamAmount != null) parts.push(`additional team ${convertToUSD(p.additionalTeamAmount)}`);
  return parts.join(' · ');
};

export default function PaymentsSection({
  d, activePaymentProducts, activeDonations, stripeConnected, onConnectStripe,
  registrationName, orgId, currentParentId, productActions,
}) {
  const { draft, setField, dirtyBySection, savingSection, saveSection } = d;

  const selectedProduct = activePaymentProducts.find((p) => p._id === draft.selectedPaymentProduct);
  const selectedDonation = activeDonations.find((p) => p._id === draft.selectedDonationProduct);
  // Fees/donations are gated on Stripe Connect. A toggle that's already ON
  // (older registration) stays interactive so the admin can turn it OFF.
  const lockOn = (flag) => !stripeConnected && !flag;

  // Inline product form state, per block: null | 'create' | 'edit'.
  const [feeForm, setFeeForm] = useState(null);
  const [donationForm, setDonationForm] = useState(null);
  const [feeUsage, setFeeUsage] = useState(null);
  const [donationUsage, setDonationUsage] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [attachHint, setAttachHint] = useState(null); // 'fee' | 'donation' | null

  // All org product names (incl. donations/deactivated) — Stripe-side name
  // collisions don't care about our filters.
  const allNames = [...activePaymentProducts, ...activeDonations].map((p) => p.name);

  // Usage caution data: everything referencing the product EXCEPT this
  // registration's own parent (editing "for yourself" needs no warning).
  const loadUsage = (productId, setUsage) => {
    setUsage(null);
    productActions.getPaymentProductUsage(productId).then((u) => {
      if (!u) return;
      const usedBy = (u.usedBy || []).filter((x) => String(x._id) !== String(currentParentId || ''));
      setUsage({ usedBy, total: usedBy.length });
    });
  };

  const createFee = async (payload) => {
    setSavingProduct(true);
    try {
      const name = autoProductName(`${registrationName || 'Registration'} Fee`, allNames);
      const result = await productActions.createPaymentProduct({ name, league: orgId, ...payload });
      if (result?._id) {
        setFeeForm(null);
        setField('selectedPaymentProduct', result._id);
        setAttachHint('fee');
      }
    } finally {
      setSavingProduct(false);
    }
  };

  const updateFee = async (payload) => {
    setSavingProduct(true);
    try {
      const result = await productActions.updatePaymentProduct({
        _id: selectedProduct._id,
        description: selectedProduct.description || '',
        ...payload,
      });
      if (result?._id) setFeeForm(null);
    } finally {
      setSavingProduct(false);
    }
  };

  const createDonation = async (payload) => {
    setSavingProduct(true);
    try {
      const name = autoProductName(`${registrationName || 'Registration'} Donation`, allNames);
      const result = await productActions.createDonationProduct({ name, league: orgId, ...payload });
      if (result?._id) {
        setDonationForm(null);
        setField('selectedDonationProduct', result._id);
        setAttachHint('donation');
      }
    } finally {
      setSavingProduct(false);
    }
  };

  const updateDonation = async (payload) => {
    setSavingProduct(true);
    try {
      // Donations are isFree (amount lives at checkout) — sending isFree keeps
      // the update path off the Stripe price legs.
      const result = await productActions.updatePaymentProduct({
        _id: selectedDonation._id,
        isFree: true,
        ...payload,
      });
      if (result?._id) setDonationForm(null);
    } finally {
      setSavingProduct(false);
    }
  };

  return (
    <SectionCard
      id="payments"
      title="Payments"
      description="What teams pay when they register."
      dirty={dirtyBySection.payments}
      saving={savingSection === 'payments'}
      onSave={() => saveSection('payments')}
    >
      {!stripeConnected && (
        <div className="rounded-lg border border-warning/30 bg-warning/10 p-4" data-testid="stripe-connect-cta">
          <div className="flex items-center gap-2 text-sm font-medium text-warning">
            <CreditCard className="h-4 w-4" /> Set up payments to collect fees
          </div>
          <p className="mt-1 text-xs text-warning">
            Registration fees and donations are paid out directly to your bank account. Set up
            once and every registration can charge.
          </p>
          <Button size="sm" variant="outline" className="mt-3" onClick={onConnectStripe}>
            <CreditCard className="mr-2 h-4 w-4" /> Set Up Payments
          </Button>
        </div>
      )}

      <div className="space-y-4 rounded-lg border p-4">
        <div className="flex items-center gap-2">
          <Switch
            id="collect-payments"
            checked={!!draft.collectPayments}
            disabled={lockOn(!!draft.collectPayments)}
            onCheckedChange={(v) => setField('collectPayments', v)}
          />
          <Label htmlFor="collect-payments" className="text-base font-medium">Registration fee</Label>
        </div>
        {draft.collectPayments && (
          <div className="space-y-3 pl-10">
            <Label htmlFor="payment-product" className="text-sm text-muted-foreground">
              Select a fixed registration fee
            </Label>
            <Select
              id="payment-product"
              value={draft.selectedPaymentProduct || ''}
              onChange={(e) => {
                if (e.target.value === CREATE_VALUE) {
                  if (stripeConnected) setFeeForm('create');
                  return;
                }
                setAttachHint(null);
                setFeeForm(null);
                setField('selectedPaymentProduct', e.target.value);
              }}
              displayEmpty
              renderValue={(selected) => {
                const product = activePaymentProducts.find((p) => p._id === selected);
                if (!product) return 'Select a fee amount';
                return `${product.name} - ${convertToUSD(product.amount)}`;
              }}
              className="w-full"
            >
              {activePaymentProducts.map((product) => (
                <MenuItem key={product._id} value={product._id}>
                  {`${product.name} - ${convertToUSD(product.amount)}`}
                </MenuItem>
              ))}
              {stripeConnected && (
                <MenuItem value={CREATE_VALUE} className="flex cursor-pointer items-center text-primary">
                  + Create a new registration fee
                </MenuItem>
              )}
            </Select>

            {attachHint === 'fee' && (
              <p className="text-sm text-success" data-testid="attach-hint-fee">
                Fee created and selected. Press Save to attach it to this registration.
              </p>
            )}

            {selectedProduct && feeForm !== 'edit' && (
              <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2">
                <span className="text-sm text-muted-foreground">{feeSummary(selectedProduct)}</span>
                {stripeConnected && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setFeeForm('edit'); loadUsage(selectedProduct._id, setFeeUsage); }}
                  >
                    <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                  </Button>
                )}
              </div>
            )}

            {feeForm === 'create' && (
              <InlineProductForm
                variant="fee"
                mode="create"
                autoName={autoProductName(`${registrationName || 'Registration'} Fee`, allNames)}
                saving={savingProduct}
                onSubmit={createFee}
                onCancel={() => setFeeForm(null)}
              />
            )}
            {feeForm === 'edit' && selectedProduct && (
              <InlineProductForm
                variant="fee"
                mode="edit"
                initialProduct={selectedProduct}
                usage={feeUsage}
                saving={savingProduct}
                onSubmit={updateFee}
                onCancel={() => setFeeForm(null)}
              />
            )}

            {selectedProduct?.perPlayer && selectedProduct?.playerPaySeparately && (
              <div className="rounded-lg border border-info/30 bg-info/10 p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-info">
                  <Info className="h-4 w-4 text-info" /> Players pay separately
                </div>
                <p className="mt-1 pl-6 text-xs text-info">
                  Each player pays individually. Change this by editing the fee above.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-4 rounded-lg border p-4">
        <div className="flex items-center gap-2">
          <Switch
            id="collect-donations"
            checked={!!draft.collectDonations}
            disabled={lockOn(!!draft.collectDonations)}
            onCheckedChange={(v) => setField('collectDonations', v)}
          />
          <Label htmlFor="collect-donations" className="text-base font-medium">Optional donations</Label>
        </div>
        {draft.collectDonations && (
          <div className="space-y-3 pl-10">
            <Label htmlFor="donation-product" className="text-sm text-muted-foreground">
              Select a donation option for teams
            </Label>
            <Select
              id="donation-product"
              value={draft.selectedDonationProduct || ''}
              onChange={(e) => {
                if (e.target.value === CREATE_VALUE) {
                  if (stripeConnected) setDonationForm('create');
                  return;
                }
                setAttachHint(null);
                setDonationForm(null);
                setField('selectedDonationProduct', e.target.value);
              }}
              displayEmpty
              renderValue={(selected) => {
                const product = activeDonations.find((p) => p._id === selected);
                if (!product) return 'Select a donation option';
                return `${product.name} (Custom Amount)`;
              }}
              className="w-full"
            >
              {activeDonations.map((product) => (
                <MenuItem key={product._id} value={product._id}>
                  {`${product.name} (Custom Amount)`}
                </MenuItem>
              ))}
              {stripeConnected && (
                <MenuItem value={CREATE_VALUE} className="flex cursor-pointer items-center text-primary">
                  + Create a new donation option
                </MenuItem>
              )}
            </Select>

            {attachHint === 'donation' && (
              <p className="text-sm text-success" data-testid="attach-hint-donation">
                Donation option created and selected. Press Save to attach it to this registration.
              </p>
            )}

            {selectedDonation && donationForm !== 'edit' && stripeConnected && (
              <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2">
                <span className="text-sm text-muted-foreground">
                  {selectedDonation.description || 'Teams choose their own donation amount during registration.'}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setDonationForm('edit'); loadUsage(selectedDonation._id, setDonationUsage); }}
                >
                  <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                </Button>
              </div>
            )}

            {donationForm === 'create' && (
              <InlineProductForm
                variant="donation"
                mode="create"
                autoName={autoProductName(`${registrationName || 'Registration'} Donation`, allNames)}
                saving={savingProduct}
                onSubmit={createDonation}
                onCancel={() => setDonationForm(null)}
              />
            )}
            {donationForm === 'edit' && selectedDonation && (
              <InlineProductForm
                variant="donation"
                mode="edit"
                initialProduct={selectedDonation}
                usage={donationUsage}
                saving={savingProduct}
                onSubmit={updateDonation}
                onCancel={() => setDonationForm(null)}
              />
            )}

            <p className="text-sm text-muted-foreground">Teams choose their own donation amount during registration.</p>
          </div>
        )}
      </div>

    </SectionCard>
  );
}
